import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  Monitor,
  ChevronDown,
  Settings,
  FlaskConical,
  ClipboardList,
  LayoutTemplate,
  HelpCircle,
  LogOut,
  CalendarDays,
  UserRound,
  RotateCcw,
  Users,
} from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useAuth } from '@/contexts/AuthContext'
import { clsx } from 'clsx'

const navigationGroups = [
  {
    label: null,
    items: [
      { name: 'Orders', shortName: 'Orders', href: '/orders', icon: ShoppingBag },
      { name: 'Events', shortName: 'Events', href: '/events', icon: CalendarDays },
    ],
  },
  {
    label: 'Master Data',
    items: [
      { name: 'Menu', shortName: 'Menu', href: '/master-menu', icon: ClipboardList },
      { name: 'Materials', shortName: 'Materials', href: '/master-materials', icon: FlaskConical },
      { name: 'Vendors', shortName: 'Vendors', href: '/vendors', icon: Users },
      { name: 'Templates', shortName: 'Templates', href: '/menu-templates', icon: LayoutTemplate },
    ],
  },
]

import { CONFIG } from '@/config'

interface SidebarProps {
  collapsed: boolean
  onCollapsedChange: (collapsed: boolean) => void
}

export default function Sidebar({ collapsed, onCollapsedChange }: SidebarProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { theme, setTheme } = useTheme()
  const { user, logout } = useAuth()

  const themeIcons = {
    light: Sun,
    dark: Moon,
    system: Monitor,
  }

  const SidebarContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className="flex h-full w-full max-w-xs flex-col bg-[var(--surface-sidebar)] border-r border-[var(--border-default)] shadow-[var(--shadow-sm)]">
      {/* Top Section */}
      <div className="flex flex-col">
        {/* Brand Header */}
        <div className={clsx(
          "flex items-center justify-between border-b border-[var(--border-default)] gap-0.5",
          collapsed && !isMobile ? "px-2 h-13" : "px-4 h-13"
        )}>
          <div className="flex items-center justify-center flex-1">
            {collapsed && !isMobile ? (
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-[var(--brand-primary-subtle)] shrink-0">
                <span className="text-xs font-bold text-[var(--brand-primary)]">
                  {CONFIG.CLIENT.name?.[0]?.toUpperCase()}
                </span>
              </div>
            ) : (
              <img 
                src={CONFIG.ASSETS.logo} 
                alt={CONFIG.CLIENT.name}
                className="h-9 shrink-0 object-contain"
              />
            )}
          </div>
          {!isMobile && (
            <button
              onClick={() => onCollapsedChange(!collapsed)}
              type="button"
              aria-label="Toggle sidebar"
              className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] transition-all duration-200 cursor-pointer"
            >
              {collapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        {/* TODO: Branch Selector - Uncomment when ready to enable multi-branch support */}
        {/* 
        {(!collapsed || isMobile) && (
          <div className="px-3 pt-4">
            <BranchSelector />
          </div>
        )}
        */}

      </div>

      {/* Navigation */}
      <nav aria-label="Primary" className="flex-1 px-3 py-4 space-y-4">
        {navigationGroups.map((group, gi) => {
          const isSetupVendors = /^\/events\/[^/]+\/setup-vendors/.test(location.pathname)
          const isSetupMenu = /^\/events\/[^/]+\/setup-menu/.test(location.pathname)
          return (
            <div key={gi}>
              {/* Group label — only when expanded */}
              {group.label && (!collapsed || isMobile) && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                  {group.label}
                </p>
              )}
              {/* Divider — only when collapsed */}
              {gi > 0 && collapsed && !isMobile && (
                <div className="border-t border-[var(--border-default)] mb-3" />
              )}
              <ul role="list" className="space-y-1">
                {group.items.map((item) => {
                  const isActive = item.href === '/events'
                    ? location.pathname === '/events' || isSetupVendors
                    : item.href === '/orders'
                    ? location.pathname === '/orders' || location.pathname.startsWith('/orders/') || isSetupMenu
                    : location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={clsx(
                          'group flex rounded-lg text-[13px] font-medium transition-all duration-200',
                          isActive
                            ? 'bg-[var(--brand-primary-subtle)] text-[var(--brand-primary)]'
                            : 'text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]',
                          collapsed && !isMobile
                            ? 'flex-col items-center justify-center px-1 py-2 gap-1'
                            : 'flex-row items-center justify-start px-3 py-2.5'
                        )}
                        onClick={() => {}}
                      >
                        <item.icon
                          className={clsx(
                            'shrink-0',
                            collapsed && !isMobile ? 'h-5 w-5' : 'h-[18px] w-[18px] mr-3',
                            isActive
                              ? 'text-[var(--brand-primary)]'
                              : 'text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]'
                          )}
                        />
                        {collapsed && !isMobile
                          ? <span className="text-[9px] font-medium leading-none text-center w-full">{item.shortName}</span>
                          : item.name
                        }
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </nav>

      {/* User Profile & Settings Section */}
      <div className="border-t border-[var(--border-default)]">
        <Menu as="div" className="relative overflow-visible" role='menu'>
          <MenuButton
            aria-label="Open account and theme menu"
            className={clsx(
              "w-full flex items-center p-3 text-left text-xs hover:bg-[var(--surface-hover)] transition-all duration-200 cursor-pointer",
              collapsed && !isMobile ? 'justify-center' : 'justify-between'
            )}>
            <div className="flex items-center">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--border-strong)]">
                <UserRound className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
              </div>
              {(!collapsed || isMobile) && (
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-xs font-medium text-[var(--text-primary)] truncate">
                    {user!.name}
                  </p>
                  <p className="text-xs text-[var(--text-secondary)] truncate">
                    {user!.contactNo}
                  </p>
                </div>
              )}
            </div>

            {/* Expandable indicator */}
            {(!collapsed || isMobile) && (
              <ChevronDown className="h-4 w-4 text-[var(--text-muted)]" />
            )}
          </MenuButton>

          <MenuItems as="div" className={clsx(
            "absolute z-50 mb-2 rounded-xl bg-[var(--surface-card)] py-2 shadow-[var(--shadow-elevated)] border border-[var(--border-default)] cursor-pointer",
            collapsed && !isMobile
              ? "left-full ml-2 bottom-0 w-56 origin-bottom-left"
              : "right-0 bottom-full w-56 origin-bottom-right"
          )}>
            {/* User Info */}
            <div className="px-3 py-2 border-b border-[var(--border-default)]">
              <p className="text-xs font-medium text-[var(--text-primary)]">
                {user!.name}
              </p>
              <p className="text-xs text-[var(--text-secondary)]">
                {user!.contactNo}
              </p>
            </div>

            {/* Theme Options */}
            <div className="py-2 border-b border-[var(--border-default)]">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Theme</div>
              {(['light', 'dark', 'system'] as const).map((themeOption) => {
                const Icon = themeIcons[themeOption]
                return (
                  <MenuItem key={themeOption}>
                    {({ focus }) => (
                      <button
                        onClick={() => setTheme(themeOption)}
                        aria-current={theme === themeOption ? 'true' : undefined}
                        className={clsx(
                          'flex w-full items-center px-3 py-2 text-xs transition-colors',
                          focus ? 'bg-[var(--surface-hover)]' : '',
                          theme === themeOption
                            ? 'text-[var(--brand-primary)] font-medium'
                            : 'text-[var(--text-secondary)]'
                        )}
                      >
                        <Icon className="mr-3 h-4 w-4" />
                        <span className="capitalize">
                          {themeOption}
                          {theme === themeOption && <span className="sr-only"> (current theme)</span>}
                        </span>
                        {theme === themeOption && (
                          <svg aria-hidden="true" className="ml-auto h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    )}
                  </MenuItem>
                )
              })}
            </div>

            {/* Account Options */}
            <div className="py-2">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => navigate('/profile')}
                    className={clsx(
                      'flex w-full items-center px-3 py-2 text-xs transition-colors',
                      focus ? 'bg-[var(--surface-hover)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                    )}
                  >
                    <Settings className="mr-3 h-4 w-4" />
                    Account Settings
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    className={clsx(
                      'flex w-full items-center px-3 py-2 text-xs transition-colors',
                      focus ? 'bg-[var(--surface-hover)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                    )}
                  >
                    <HelpCircle className="mr-3 h-4 w-4" />
                    Support
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => {
                      sessionStorage.clear()
                      window.location.reload()
                    }}
                    className={clsx(
                      'flex w-full items-center px-3 py-2 text-xs transition-colors',
                      focus ? 'bg-[var(--surface-hover)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                    )}
                  >
                    <RotateCcw className="mr-3 h-4 w-4" />
                    Reset filters
                  </button>
                )}
              </MenuItem>
              <div className="border-t border-[var(--border-default)] mt-2 pt-2">
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={() => {
                        logout()
                        navigate('/login', { replace: true })
                      }}
                      className={clsx(
                        'flex w-full items-center px-3 py-2 text-xs transition-colors',
                        focus ? 'bg-[var(--surface-hover)] text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                      )}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign out
                    </button>
                  )}
                </MenuItem>
              </div>
            </div>
          </MenuItems>
        </Menu>
      </div>
    </div>
  )

  return (
    <div className={clsx(
      "hidden md:fixed md:inset-y-0 md:z-50 md:flex md:flex-col transition-all duration-300",
      collapsed ? "md:w-20" : "md:w-60"
    )}>
      <SidebarContent />
    </div>
  )
} 